import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ChevronDown, X } from "lucide-react";

// Both official WhatsApp channels for Arvind Printers
export const WA_NUMBERS = [
  {
    key: "business",
    label: "WhatsApp Business",
    number: "918390063999",
    display: "+91 83900 63999",
    subtitle: "Fastest for quotes & briefs",
  },
  {
    key: "ops",
    label: "Ops · Ajay Linge",
    number: "918806003999",
    display: "+91 88060 03999",
    subtitle: "Direct line to operations",
  },
];

export function buildWhatsAppUrl(number, message = "") {
  const msg = message
    ? message
    : "Hi Arvind Printers, I'd like to request a quote. Please share details.";
  return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
}

// A brand-styled inline pill. Opens a small popover listing both numbers.
export function WhatsAppPill({
  label = "Chat on WhatsApp",
  message,
  variant = "light", // "light" | "dark"
  className = "",
  testId = "wa-pill",
}) {
  const [open, setOpen] = useState(false);

  const base =
    "relative inline-flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] tracking-[0.22em] uppercase border transition-all duration-300";
  const light =
    "bg-[#25D366] text-[#0A0A0A] border-[#0A0A0A] hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#0A0A0A]";
  const dark =
    "bg-[#25D366] text-[#0A0A0A] border-[#F4F4F0] hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#F4F4F0]";

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className={`${base} ${variant === "dark" ? dark : light}`}
      >
        <MessageCircle className="w-3.5 h-3.5" />
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div
              className="fixed inset-0 z-[70]"
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="absolute z-[71] mt-3 min-w-[280px] border border-black bg-[#F4F4F0] text-black shadow-[8px_8px_0_0_#0A0A0A]"
              data-testid={`${testId}-menu`}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-black bg-[#0A0A0A] text-[#F4F4F0]">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase">
                  Pick a channel
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="w-6 h-6 border border-[#F4F4F0] flex items-center justify-center"
                  aria-label="Close"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <ul>
                {WA_NUMBERS.map((n) => (
                  <li key={n.key} className="border-b border-black/15 last:border-b-0">
                    <a
                      href={buildWhatsAppUrl(n.number, message)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${testId}-option-${n.key}`}
                      className="flex items-center gap-3 px-4 py-4 hover:bg-black hover:text-[#F4F4F0] transition-colors"
                      onClick={() => setOpen(false)}
                    >
                      <span className="inline-flex items-center justify-center w-9 h-9 border border-current shrink-0">
                        <MessageCircle className="w-4 h-4" />
                      </span>
                      <span className="flex-1">
                        <span className="font-display text-lg leading-tight block">
                          {n.label}
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-70">
                          {n.display} · {n.subtitle}
                        </span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Fixed floating bubble — always available, bottom-right.
export function WhatsAppFloat() {
  const [open, setOpen] = useState(false);
  return (
    <div className="fixed bottom-5 right-5 z-[75]">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.22 }}
            className="mb-3 border border-black bg-[#F4F4F0] text-black shadow-[8px_8px_0_0_#0A0A0A] min-w-[280px]"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-black bg-[#0A0A0A] text-[#F4F4F0]">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase">
                Chat with the press
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-6 h-6 border border-[#F4F4F0] flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <ul>
              {WA_NUMBERS.map((n) => (
                <li key={n.key} className="border-b border-black/15 last:border-b-0">
                  <a
                    href={buildWhatsAppUrl(n.number)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`wa-float-option-${n.key}`}
                    className="flex items-center gap-3 px-4 py-4 hover:bg-black hover:text-[#F4F4F0] transition-colors"
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 border border-current shrink-0">
                      <MessageCircle className="w-4 h-4" />
                    </span>
                    <span className="flex-1">
                      <span className="font-display text-lg leading-tight block">
                        {n.label}
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-70">
                        {n.display}
                      </span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="wa-float"
        aria-label="Chat on WhatsApp"
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1.5, duration: 0.6, type: "spring" }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="w-14 h-14 rounded-full bg-[#25D366] border-2 border-[#0A0A0A] shadow-[4px_4px_0_0_#0A0A0A] flex items-center justify-center relative"
      >
        <MessageCircle className="w-6 h-6 text-[#0A0A0A]" />
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#EC008C] border border-[#0A0A0A]" />
      </motion.button>
    </div>
  );
}
